// src/data/feedRankingService.js
//
// Reads and saves the feed ranking settings, per country (Roadmap #104,
// docs/briefs/2026-10-18-feed-dam-ranking-addendum-1.md; the Events look-ahead half-life since
// docs/briefs/2026-10-18-feed-dam-ranking-phase2-events.md § Settings). The step shares rank News and
// Events; the half-life is News's, the event half-life Events'. Both calls go through admin-only
// functions that check is_platform_admin() themselves; the table has no client access
// (supabase/migrations/20261018_feed_dam_ranking.sql, 20261018b_feed_dam_ranking_events.sql). A save
// is logged by the database.

import { supabaseFetch } from '../lib/supabase';

/**
 * Every country's settings, as percentages and hours, its time zone (set in the database, not here),
 * and when and by whom each was last changed.
 * @returns {Promise<{ rows: Array<{country_code: string, keep_vertical_pct: number, keep_horizontal_pct: number,
 *   half_life_hours: number, event_half_life_hours: number, timezone: string, updated_at: string,
 *   updated_by_name: string|null}>, error?: string }>}
 */
export async function getFeedRankingSettings() {
  try {
    const { data, error } = await supabaseFetch.rpc('admin_get_feed_ranking_params', {});
    if (error) {
      console.error('Failed to load feed ranking settings:', error);
      return { rows: [], error: error.message || 'Could not load the settings.' };
    }
    return {
      rows: (data || []).map(row => ({
        ...row,
        country_code: (row.country_code || '').trim(),
        keep_vertical_pct: Number(row.keep_vertical_pct),
        keep_horizontal_pct: Number(row.keep_horizontal_pct),
        half_life_hours: Number(row.half_life_hours),
        event_half_life_hours: Number(row.event_half_life_hours)
      }))
    };
  } catch (error) {
    console.error('Exception loading feed ranking settings:', error);
    return { rows: [], error: 'Could not load the settings.' };
  }
}

/**
 * Save one country's settings. Takes effect on the next feed load.
 * @param {string} countryCode - e.g. 'PT'
 * @param {number} keepVerticalPct - share kept per vertical step, 1–100
 * @param {number} keepHorizontalPct - share kept per horizontal step, 1–100
 * @param {number} halfLifeHours - News half-life, 1–8760
 * @param {number} eventHalfLifeHours - Events look-ahead half-life, 1–8760. Always sent: an omitted
 *   value would reach the database as NULL, which keeps the current one, and the screen would say
 *   "Saved" for a change that did not happen (Advisor A1).
 * @returns {Promise<{ row?: object, error?: string }>}
 */
export async function saveFeedRankingSettings(countryCode, keepVerticalPct, keepHorizontalPct, halfLifeHours, eventHalfLifeHours) {
  if (eventHalfLifeHours === undefined || eventHalfLifeHours === null || Number.isNaN(eventHalfLifeHours)) {
    return { error: 'The event look-ahead half-life is required.' };
  }
  try {
    const { data, error } = await supabaseFetch.rpc('admin_set_feed_ranking_params', {
      p_country_code: countryCode,
      p_keep_vertical_pct: keepVerticalPct,
      p_keep_horizontal_pct: keepHorizontalPct,
      p_half_life_hours: halfLifeHours,
      p_event_half_life_hours: eventHalfLifeHours
    });
    if (error) {
      console.error('Failed to save feed ranking settings:', error);
      return { error: error.message || 'Could not save the settings.' };
    }
    return { row: Array.isArray(data) ? data[0] : data };
  } catch (error) {
    console.error('Exception saving feed ranking settings:', error);
    return { error: 'Could not save the settings.' };
  }
}
